import { useFormik } from 'formik';
import { useContext, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { roleContext } from '../../context/RoleContext';
import {
  barberUpdatePersonalDetails,
  shopBarberUpdatePersonalDetails,
  shopOwnerUpdateDetails,
} from '../../middleware/registration_service/updateRequest';
import { UserRole } from '../../types/role';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { orange } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box } from '@mui/system';
import Select from '@mui/material/Select';
import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useProfileData } from '../../context/ProfileDataContext';

// Add validation schema
const schema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phoneNumber: Yup.string().required('Phone number is required'),
  title: Yup.string().required('Title is required'),
});

export default function InfoChangeDialog() {
  const id = localStorage.getItem('reg_partner_id');
  const partnerId = BigInt(id ? id : 0);
  const [open, setOpen] = useState(false);
  const role = useContext(roleContext);
  const { partner } = useProfileData();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();

  // Pre-populate the form with the currently stored partner details so the
  // dialog never opens blank.
  const initialValues = useMemo(
    () => ({
      firstName: partner?.firstName || '',
      lastName: partner?.lastName || '',
      phoneNumber: partner?.phoneNumber || '',
      title: partner?.title || '',
    }),
    [partner]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values) => {
      setError(null);
      const barberId = BigInt(id ? id : 0);

      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        if (role === UserRole.BARBER) {
          await barberUpdatePersonalDetails(
            values.firstName,
            values.lastName,
            values.phoneNumber,
            values.title,
            barberId,
            token
          );
        } else if (role === UserRole.SHOP_BARBER) {
          await shopBarberUpdatePersonalDetails(
            values.firstName,
            values.lastName,
            values.phoneNumber,
            values.title,
            barberId,
            token
          );
        } else if (role === UserRole.SHOP_OWNER) {
          await shopOwnerUpdateDetails(
            values.firstName,
            values.lastName,
            values.phoneNumber,
            values.title,
            partnerId,
            token
          );
        }

        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          handleClose();
          window.location.reload();
        }, 3000);
      } catch (error) {
        console.error('Error updating details:', error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    formik.resetForm();
  };

  return (
    <React.Fragment>
      <Button
        className="buttonHover"
        sx={{
          px: 0.8,
          py: 0.8,
          width: '100%',
          background: orange[800],
          color: '#fff',
          justifyContent: 'center',
          fontFamily: 'GFS Didot',
          fontWeight: 'bold',
        }}
        variant="contained"
        onClick={handleClickOpen}
      >
        Edit
      </Button>

      <Dialog
        className="shopProfileDialog"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDialog-paper': {
            width: '80%',
            maxWidth: '600px',
          },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
            id="customized-dialog-title"
          >
            <PersonOutlineIcon sx={{ color: orange[800] }} />
            Personal Information
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="firstName"
                    fullWidth
                    id="firstName"
                    label="First name"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.firstName &&
                      Boolean(formik.errors.firstName)
                    }
                    helperText={
                      formik.touched.firstName && formik.errors.firstName
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="lastName"
                    label="Last name"
                    name="lastName"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.lastName && Boolean(formik.errors.lastName)
                    }
                    helperText={
                      formik.touched.lastName && formik.errors.lastName
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phoneNumber"
                    fullWidth
                    id="phoneNumber"
                    label="Phone number"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.phoneNumber &&
                      Boolean(formik.errors.phoneNumber)
                    }
                    helperText={
                      formik.touched.phoneNumber && formik.errors.phoneNumber
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Select
                    fullWidth
                    id="title"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    displayEmpty
                    error={formik.touched.title && Boolean(formik.errors.title)}
                  >
                    <MenuItem value="">
                      <em>Select Title</em>
                    </MenuItem>
                    <MenuItem value="Mr">Mr</MenuItem>
                    <MenuItem value="Mrs">Mrs</MenuItem>
                    <MenuItem value="Miss">Miss</MenuItem>
                    <MenuItem value="Dr">Dr</MenuItem>
                    <MenuItem value="Prof">Prof</MenuItem>
                  </Select>
                  {formik.touched.title && formik.errors.title && (
                    <Box
                      sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}
                    >
                      {formik.errors.title}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              className="buttonHover"
              type="submit"
              disabled={submitted}
              fullWidth
              variant="contained"
              sx={{
                mt: '2em',
                mr: '15px',
                ml: '15px',
                mb: '10px',
                bgcolor: '#2b2b33',
                color: '#fff',
                p: 1,
                width: '100%',
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Save'
              )}
            </Button>
          </DialogActions>
        </form>
        {submitted && (
          <Alert
            onClose={() => setSubmitted(false)}
            severity="success"
            sx={{ mt: '0.5em' }}
          >
            Profile updated successfully
          </Alert>
        )}
        {error && (
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ mt: '0.5em' }}
          >
            {error}
          </Alert>
        )}
      </Dialog>
    </React.Fragment>
  );
}
